import { useCallback } from 'react';
import { Granit } from 'src/services/granits';
import { BucketsUrls } from 'src/services/file';
import { getGlobalIndex } from 'src/utils/3d.utils';
import {
  changeTexture,
  selectPieceWithGranitId,
  applyStockGranits,
  StockToApply,
} from './utils';

type UseChangeGranitParams = {
  pieceIndex: number;
  activePieces: any[];
  customSteles: any[];
  customPlatings: any[];
  customSoles: any[];
  customBoxes: any[];
  pieces: any[];
  granits: Granit[];
  buckets: BucketsUrls | null;
  hasAdvancedMode: boolean;
  selectedDominant: string | undefined;
  hasSelectedAllPieces: boolean;
  setActivePieces: (pieces: any[]) => void;
  setCustomSteles: (steles: any[]) => void;
  setCustomPlatings: (platings: any[]) => void;
  setCustomSoles: (soles: any[]) => void;
  setCustomBoxes: (boxes: any[]) => void;
  setError: (error: string | undefined) => void;
  setSelectedDominant: (dominant: string | undefined) => void;
  setPieceIndex: (index: number) => void;
  setIsStockModalOpen: (open: boolean) => void;
};

const getBuckets = (buckets: BucketsUrls | null) =>
  buckets ?? { BUCKET_3D_PIECES: '' };

/**
 * Collect global indices for all custom elements (steles, platings, soles, boxes)
 * with an optional filter on the element.
 */
const collectCustomIndices = (
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  filterFn?: (element: any) => boolean,
) => {
  const indices: number[] = [];

  const collections = [
    { items: customSteles, type: 'customStele' as const },
    { items: customPlatings, type: 'customPlating' as const },
    { items: customSoles, type: 'customSole' as const },
    { items: customBoxes, type: 'customBox' as const },
  ];

  for (const { items, type } of collections) {
    for (const [elementIndex, element] of items.entries()) {
      if (!filterFn || filterFn(element)) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          type,
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        indices.push(globalIndex + elementIndex);
      }
    }
  }

  return indices;
};

export const useChangeGranit = (params: UseChangeGranitParams) => {
  const {
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
    pieces,
    granits,
    buckets,
    hasAdvancedMode,
    selectedDominant,
    hasSelectedAllPieces,
    setActivePieces,
    setCustomSteles,
    setCustomPlatings,
    setCustomSoles,
    setCustomBoxes,
    setError,
    setSelectedDominant,
    setPieceIndex,
    setIsStockModalOpen,
  } = params;

  const applyTexture = useCallback(
    (granit: Granit, piecesIdsToChange: number[]) => {
      changeTexture(
        granit,
        piecesIdsToChange,
        getBuckets(buckets),
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
        pieces,
        hasAdvancedMode,
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setCustomBoxes,
        setError,
      );
    },
    [
      buckets,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
      pieces,
      hasAdvancedMode,
      setActivePieces,
      setCustomSteles,
      setCustomPlatings,
      setCustomSoles,
      setCustomBoxes,
      setError,
    ],
  );

  const handleChangeGranit = useCallback(
    (granit: Granit) => {
      setError(undefined);

      if (selectedDominant) {
        // Collect active pieces matching dominant granit
        const piecesIdsToChange: number[] = [];
        for (const [elementIndex, element] of activePieces.entries()) {
          if (element.granit?.id === selectedDominant) {
            if (element.underAssembly) {
              if (
                element.underAssembly.fabrications.includes(granit.fabrication)
              )
                piecesIdsToChange.push(elementIndex);
            } else if (element.fabrications.includes(granit.fabrication)) {
              piecesIdsToChange.push(elementIndex);
            }
          }
        }

        // Collect custom elements matching dominant granit
        const customIndices = collectCustomIndices(
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
          (element) => element.granit?.id === selectedDominant,
        );
        piecesIdsToChange.push(...customIndices);

        applyTexture(granit, piecesIdsToChange);
        setSelectedDominant(granit.id);
      } else if (hasSelectedAllPieces) {
        // Collect active pieces matching fabrication
        const piecesIdsToChange: number[] = [];
        for (const [elementIndex, element] of activePieces.entries()) {
          if (
            element.fabrications?.includes(granit.fabrication) ||
            (element.underAssembly?.fabrications?.includes(
              granit.fabrication,
            ) &&
              element.material === granit.material)
          ) {
            piecesIdsToChange.push(elementIndex);
          }
        }

        // Collect all custom elements (no filter)
        const customIndices = collectCustomIndices(
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        piecesIdsToChange.push(...customIndices);

        applyTexture(granit, piecesIdsToChange);
      } else {
        applyTexture(granit, [pieceIndex]);
      }
    },
    [
      selectedDominant,
      hasSelectedAllPieces,
      pieceIndex,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
      applyTexture,
      setError,
      setSelectedDominant,
    ],
  );

  const handleSelectPieceWithGranitId = useCallback(
    (granitId: string) => {
      selectPieceWithGranitId(
        granitId,
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
        setPieceIndex,
      );
    },
    [
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
      setPieceIndex,
    ],
  );

  const handleApplyStock = useCallback(
    (stock: StockToApply) => {
      setError(undefined);
      applyStockGranits(
        stock,
        granits,
        getBuckets(buckets),
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
        pieces,
        hasAdvancedMode,
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setCustomBoxes,
        setError,
      );
      setIsStockModalOpen(false);
    },
    [
      granits,
      buckets,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
      pieces,
      hasAdvancedMode,
      setActivePieces,
      setCustomSteles,
      setCustomPlatings,
      setCustomSoles,
      setCustomBoxes,
      setError,
      setIsStockModalOpen,
    ],
  );

  return {
    handleChangeGranit,
    handleSelectPieceWithGranitId,
    handleApplyStock,
  };
};
